import { trackMiscAction } from 'analytics';
import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ColorInput from 'components/Widget/Input/ColorInput';
import OptionRow from 'components/Widget/Input/OptionRow';
import { DropShadowMode, Scene, SceneMetadata } from 'dragonmouse-shared/interfaces';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { useEffect } from 'react';
import { DEFAULT_CANVAS_COLOR, DEFAULT_CANVAS_DROP_SHADOW_MODE } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import Section from '../Section';
import * as Base from '../styles';
import * as S from './styles';

interface ScenePropertiesProps {
	scene: Scene;
}
const SceneProperties = ({ scene }: ScenePropertiesProps) => {
	const canvasColor = scene?.metadata?.canvasColor
		? scene.metadata.canvasColor
		: DEFAULT_CANVAS_COLOR;

	// Debounce the property changes to avoid spam during dragging
	const debouncedCanvasColor = useDebouncedValue(canvasColor);
	const debouncedCanvasDropShadowMode = useDebouncedValue(scene?.metadata?.canvasDropShadowMode);
	const canvasDropShadowMode = scene?.metadata?.canvasDropShadowMode
		? scene.metadata.canvasDropShadowMode
		: DEFAULT_CANVAS_DROP_SHADOW_MODE;

	// Track debounced property changes
	useEffect(() => {
		if (debouncedCanvasColor !== canvasColor) {
			trackMiscAction('change_scene_background_color');
		}
	}, [debouncedCanvasColor, canvasColor]);

	useEffect(() => {
		if (debouncedCanvasDropShadowMode !== scene?.metadata?.canvasDropShadowMode) {
			trackMiscAction('change_scene_drop_shadow_mode');
		}
	}, [debouncedCanvasDropShadowMode, scene?.metadata?.canvasDropShadowMode]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onPropertyChange = async (property: 'canvasColor' | 'canvasDropShadowMode', value: any) => {
		if (!scene) return;

		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		forge.dbrUpdateScene({
			update: {
				id: scene.id,
				metadata: {
					...(scene.metadata as SceneMetadata),
					[property]: value,
				},
			},
			showToast: true,
		});
	};

	//----------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Base.Base>
			<Base.ScrollingContainer>
				<Section key={'page'} label="Background">
					<Base.Row>
						<ColorInput
							color={canvasColor}
							onColorChange={(color) => {
								onPropertyChange('canvasColor', color);
							}}
						/>
					</Base.Row>
					<S.LilSpacer />
					<Base.Row>
						<OptionRow
							label={IconType.SHADOW}
							values={['none', 'light', 'medium', 'heavy']}
							value={canvasDropShadowMode}
							tooltip={'Drop shadow that appears behind frames'}
							onChange={(value: string) => {
								onPropertyChange('canvasDropShadowMode', value as DropShadowMode);
							}}
						/>
					</Base.Row>
					<Base.Spacer />
				</Section>
				<Base.Spacer />
			</Base.ScrollingContainer>
		</Base.Base>
	);
};

export default SceneProperties;
